import { rangeTo } from '@/utils/array';
import type { IPaginationProps } from './types';
import ChevronIcon from 'assets/img/svg/icons/chevron_left.svg?url';
import type { IconFactor } from '@/types/elements/icon';
import Icon from '../icon';
import { tv } from 'tailwind-variants';
import { useResponsive } from '~/composables/useResponsive';
import SelectDropDown from 'components/select-dropdown';
import { DEFAULT_ITEMS_PER_PAGE, DEFAULT_ITEMS_PER_PAGE_OPTIONS } from '@/types/pagination';

const PageButton = defineComponent<{
  selected?: boolean;
  class?: string;
  onClick?: any;
  disabled?: boolean;
  icon?: IconFactor;
}>(
  (props, { slots }) => {
    return () => {
      const defaultClass = `flex
                      items-center
                      justify-center
                      px-3 h-8
                      leading-tight
                      text-gray-500
                      bg-white border
                      border-gray-300
                      hover:bg-gray-100
                      hover:text-gray-700
                      dark:bg-gray-800
                      dark:border-gray-700
                      dark:text-gray-400
                      dark:hover:bg-gray-700
                      dark:hover:text-white`;

      const selectedClass = `flex
                        items-center
                        justify-center
                        px-3
                        h-8
                        text-primary-600
                        border border-gray-300
                        bg-primary-50
                        hover:bg-primary-100
                        hover:text-primary-700
                        dark:border-gray-700
                        dark:bg-gray-700
                        dark:text-white`;

      return (
        <li
          onClick={props.onClick}
          style={[
            props.disabled
              ? {
                opacity: '40%',
              }
              : {},
          ]}
        >
          <span
            aria-current={props.selected ? 'page' : 'false'}
            class={(props.selected ? selectedClass : defaultClass) + ` ${props.class || ''}`}
            style={{
              cursor: 'pointer',
              userSelect: 'none',
            }}
          >
            {props.icon ? <Icon src={props.icon} /> : slots.default ? slots.default() : ''}
          </span>
        </li>
      );
    };
  },
  { props: ['selected', 'class', 'onClick', 'disabled', 'icon'] },
);

const PaginationClass = tv({
  base: 'flex items-center flex-column flex-wrap md:flex-row gap-4 py-4 px-4',
  variants: {
    color: {
      white: 'bg-white',
    },
    noPadding: {
      true: 'px-0 py-0',
    },
  },
});

const Pagination = defineComponent<IPaginationProps>({
  setup(props) {
    const responsive = useResponsive();
    const pageNumbers = computed(() => {
      if (props.infinite) {
        return props.pageNumber >= 1
          ? [props.pageNumber - 1, props.pageNumber, props.pageNumber + 1]
          : [props.pageNumber, props.pageNumber + 1, props.pageNumber + 2];
      }

      if ((props.pageCount || 0) <= 2) return [0, 1];
      const x = props.pageNumber >= 1 ? 1 : 0;
      let start = props.pageNumber - x;
      let end = props.pageNumber + (3 - x);

      if (props.pageNumber >= (props.pageCount || 1) - 1) {
        start -= 1;
        end -= 1;
      }
      //if (props.pageNumber <= 0 && (props.pageCount - props.pageNumber) > 4) {
      //  start = 0;
      //  end = props.pageNumber + 4;
      //}

      return rangeTo(start, end);
    });

    const itemsPerPageOptions = computed(() => {
      const options = props.itemsPerPageOptions || DEFAULT_ITEMS_PER_PAGE_OPTIONS;
      return options.map((value) => ({
        key: `${value}`,
        label: `${value}`,
        value: value,
      }));
    });

    const handleItemsPerPageChange = (newValue: number) => {
      if (props.onItemsPerPageChange) {
        props.onItemsPerPageChange(newValue);
      }
    };

    const handlePageChange = () => {
      if (props.onPageChange) {
        props.onPageChange();
      }
    };

    const handleGoBack = () => {
      queueMicrotask(() => {
        if (props.pageNumber <= 0) return;
        if (props.onGoBack) {
          handlePageChange();
          props.onGoBack();
        }
      });
    };

    const handleGoNext = () => {
      queueMicrotask(() => {
        if (typeof props.pageCount === 'number' && props.pageNumber >= props.pageCount - 1) return;
        if (props.onGoNext) {
          handlePageChange();
          props.onGoNext();
        }
      });
    };

    const handleSelectPage = (i: number) => {
      queueMicrotask(() => {
        if (props.onSelectPage) {
          handlePageChange();
          props.onSelectPage(i);
        }
      });
    };

    return () => {
      const icon = (flip?: boolean): IconFactor => ({
        src: ChevronIcon,
        fill: 'currentColor',
        size: 10,
        flipHori: flip,
      });

      return (
        <nav
          class={PaginationClass({ color: props.variant as any, noPadding: !!props.noPadding })}
          aria-label="Table navigation"
        >
          <ul class="inline-flex -space-x-px rtl:space-x-reverse text-sm h-8">
            <PageButton disabled={props.pageNumber <= 0} icon={icon()} onClick={handleGoBack}>
              Previous
            </PageButton>
            {pageNumbers.value.map((i) => {
              const disabled = props.infinite ? false : i < 0 || i >= (props.pageCount || 1);
              const selected = i === props.pageNumber;
              return (
                <PageButton
                  onClick={disabled ? undefined : () => handleSelectPage(i)}
                  disabled={disabled}
                  key={`pagination-${i}-${selected}`}
                  selected={selected}
                >
                  {i + 1}
                </PageButton>
              );
            })}
            <PageButton
              disabled={props.infinite ? false : props.pageNumber >= (props.pageCount || 1) - 1}
              icon={icon(true)}
              onClick={handleGoNext}
            >
              Next
            </PageButton>
          </ul>
          <div class="flex items-center gap-2.5">
            {props.totalItemCount && props.pageCount && props.onItemsPerPageChange ? (
              <div class="flex items-center gap-4 flex-wrap">
                <span class="text-sm font-normal text-gray-500 ">Items per page:</span>
                <div style={{ minWidth: '70px' }}>
                  <SelectDropDown
                    size="xs"
                    variant="white"
                    options={{
                      items: itemsPerPageOptions.value,
                      search: { enabled: false },
                    }}
                    value={props.itemsPerPage || DEFAULT_ITEMS_PER_PAGE}
                    onValueChange={handleItemsPerPageChange}
                  />
                </div>
                <span
                  style={{ ...(responsive.isSmall.value ? { margin: '0px' } : {}) }}
                  class="text-sm font-normal text-gray-500 dark:text-gray-400"
                >
                  <span class="font-medium text-gray-900 dark:text-white">
                    {Math.min(props.itemsPerPage * props.pageNumber + 1, props.totalItemCount)}-
                    {Math.min(props.itemsPerPage * (props.pageNumber + 1), props.totalItemCount)}
                  </span>{' '}
                  of{' '}
                  <span class="font-medium text-gray-900 dark:text-white">
                    {props.totalItemCount}
                  </span>
                </span>
              </div>
            ) : (
              <span></span>
            )}
          </div>
        </nav>
      );
    };
  },
});

Pagination.props = [
  'pageCount',
  'pageNumber',
  'itemsPerPage',
  'onGoBack',
  'onGoNext',
  'onSelectPage',
  'onPageChange',
  'totalItemCount',
  'infinite',
  'variant',
  'noPadding',
  'itemsPerPageOptions',
  'onItemsPerPageChange',
];

export default Pagination;
